import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Modal, Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../../css/components/Custom/NewUpdates.css";
import { useTranslation } from "react-i18next";
import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useService } from 'contexts/ServiceContextProvider';
import { useCachedImage } from '../../cache/useCachedImage';

const cardVariants = {
    enter: (direction) => ({
        x: direction === 1 ? -300 : 300,
        opacity: 0,
        scale: 0.8,
        rotate: direction === 1 ? -15 : 15,
    }),
    center: {
        x: 0,
        opacity: 1,
        scale: 1,
        rotate: 0,
    },
    exit: (direction) => ({
        x: direction === 1 ? 300 : -300,
        opacity: 0,
        scale: 0.8,
        rotate: direction === 1 ? 15 : -15,
    }),
};

const modalStyle = {
    position: "absolute",
    width: "100%",
    height: "100%",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    outline: "none",
    display: "flex",
    flexDirection: "column",
};

const CachedImage = ({ src, alt, ...props }) => {
    const cachedUrl = useCachedImage(src);
    return <img src={cachedUrl} alt={alt} {...props} />;
};

const NewUpdatesModal = ({ isOpen: externalIsOpen, setIsOpen: externalSetIsOpen }) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = externalIsOpen !== undefined && externalSetIsOpen !== undefined;
    const isOpen = isControlled ? externalIsOpen : internalIsOpen;
    const setIsOpen = isControlled ? externalSetIsOpen : setInternalIsOpen;

    const [updates, setUpdates] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [, setHasPrevPage] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [latestDate, setLatestDate] = useState(null);
    const [lastSeenVersion, setLastSeenVersion] = useState(null);

    const workspaceContext = useWorkspaces();
    const loading = workspaceContext.loading;

    const getCurrentlyLoggedUser = workspaceContext.getCurrentlyLoggedUser();

    const { t } = useTranslation();
    const { getUpdateNews, markUpdatesAsSeen } = useService();

    const modalRef = useRef(null);
    const hasOpenedRef = useRef(false);
    const [direction, setDirection] = useState(0);
    const [isFirstFetchDone, setIsFirstFetchDone] = useState(false);


    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const fetchInitialUpdates = async () => {
            if (!loading) {
                const fetchedResults = await fetchUpdates(1);
                if (fetchedResults.length > 0 && lastSeenVersion) {
                    const latestUpdateDate = fetchedResults[0].date;
                    setLatestDate(latestUpdateDate);
                    if ((!lastSeenVersion || new Date(lastSeenVersion) < new Date(latestUpdateDate)) && !hasOpenedRef.current) {
                        setInternalIsOpen(true);
                        hasOpenedRef.current = true;
                    }
                }
            }
        };

        fetchInitialUpdates();

    }, [isControlled, getUpdateNews, lastSeenVersion, loading]);

    const fetchUpdates = async (page = 1) => {
        setIsFetching(true);
        try {
            const response = await getUpdateNews({ page, page_size: 5 });
            setUpdates((prev) =>
                page === 1 ? response.results : [...prev, ...response.results]
            );
            setCurrentPage(page);
            setHasNextPage(response.next !== null);
            setHasPrevPage(response.previous !== null);
            setLastSeenVersion(getCurrentlyLoggedUser?.last_seen_version);
            return response.results;
        } catch (error) {
            console.error("Error fetching updates:", error);
            return [];
        } finally {
            setIsFetching(false);
            setIsFirstFetchDone(true);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowLeft") {
                nextUpdate();
            } else if (e.key === "ArrowRight") {
                prevUpdate();
            } else if (e.key === "Escape") {
                handleClose();
            }
        };

        if (isOpen) {
            window.addEventListener("keydown", handleKeyDown);
            if (modalRef.current) {
                modalRef.current.focus();
            }
        } else {
            window.removeEventListener("keydown", handleKeyDown);
        }

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, currentIndex, hasNextPage]);

    const handleClose = async () => {
        setIsOpen(false);

        const latestDateObj = new Date(latestDate);
        const lastSeenVersionObj = new Date(lastSeenVersion);

        if (latestDateObj.getTime() !== lastSeenVersionObj.getTime() && latestDate) {
            try {
                await markUpdatesAsSeen();
                setLastSeenVersion(latestDate);
            } catch (error) {
                console.error("Error updating last_seen_version:", error);
            }
        }
        setCurrentIndex(0);
    };

    const nextUpdate = async () => {
        if (currentIndex < updates.length - 2) {
            setDirection(1);
            setCurrentIndex((prev) => prev + 1);
        } else if (hasNextPage && !isFetching) {
            const fetchedResults = await fetchUpdates(currentPage + 1);
            if (fetchedResults.length > 0) {
                setDirection(1);
                setCurrentIndex((prev) => prev + 1);
            }
        } else if (currentIndex < updates.length - 1) {
            setDirection(1);
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const prevUpdate = () => {
        if (currentIndex > 0) {
            setDirection(-1);
            setCurrentIndex((prev) => prev - 1);
        }
    };

    return (
        <Modal
            open={isOpen}
            onClose={(event, reason) => {
                if (reason === "backdropClick" || reason === "escapeKeyDown") {
                    return;
                }
                handleClose();
            }}
            aria-labelledby="new-updates-modal-title"
            aria-describedby="new-updates-modal-description"
            disableAutoFocus
            disableEnforceFocus
        >
            <Box sx={modalStyle} ref={modalRef} tabIndex="-1">

                <div className="updates-container">
                    <IconButton
                        className="close-button"
                        onClick={handleClose}
                        aria-label="close"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'red',
                        }}
                    >
                        <CloseIcon
                            style={{
                                fontSize: '32px',
                            }} />
                    </IconButton>

                    {(!isFirstFetchDone || isFetching) && updates.length === 0 ? (
                        <div className="loading-indicator">
                            <Typography variant="body1" >{t("news.loading")}</Typography>
                        </div>
                    ) : updates.length === 0 ? (
                        <div className="no-news">
                            <Typography variant="h3">{t("news.no_news")}</Typography>
                            <p style={{ marginTop: "10px" }}>{t("news.no_news_desc")}</p>
                        </div>
                    ) : (
                        <>
                            {/* AnimatePresence for the center card */}
                            <AnimatePresence custom={direction} initial={false}>
                                {updates.length > 0 && (
                                    <motion.div
                                        key={updates[currentIndex].id}
                                        className="card center-card"
                                        custom={direction}
                                        variants={cardVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.5 }}
                                    >
                                        {updates[currentIndex]?.image && (
                                            <CachedImage
                                                src={updates[currentIndex].image}
                                                alt={updates[currentIndex].title}
                                            />
                                        )}

                                        <h2>{updates[currentIndex].title}</h2>
                                        <p className="date">{updates[currentIndex].date}</p>
                                        <div
                                            className="content"
                                            dangerouslySetInnerHTML={{
                                                __html: updates[currentIndex].content,
                                            }}
                                        ></div>
                                        <div className="card-footer">
                                            Workflow Pro - © {new Date().getFullYear()}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Side Cards */}
                            {/* Previous Card (Older Updates) */}
                            {currentIndex < updates.length - 1 && (
                                <div className="side-card left-side" onClick={nextUpdate} role="button" tabIndex={0}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            nextUpdate();
                                        }
                                    }}>
                                    <motion.div
                                        className="card left-card"
                                        initial={{ opacity: 0, scale: 0.8, rotate: 15 }}
                                        animate={{ opacity: 0.5, scale: 0.9, rotate: 15 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {updates[currentIndex + 1]?.image && (
                                            <CachedImage
                                                src={updates[currentIndex + 1].image}
                                                alt={updates[currentIndex + 1].title}
                                            />
                                        )}
                                        <h2>{updates[currentIndex + 1].title}</h2>
                                        <p className="date">{updates[currentIndex + 1].date}</p>
                                    </motion.div>
                                </div>
                            )}

                            {/* Next Card (Newer Updates) */}
                            {currentIndex > 0 && (
                                <div className="side-card right-side" onClick={prevUpdate} role="button" tabIndex={0}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            prevUpdate();
                                        }
                                    }}>
                                    <motion.div
                                        className="card right-card"
                                        initial={{ opacity: 0, scale: 0.8, rotate: -15 }}
                                        animate={{ opacity: 0.5, scale: 0.9, rotate: -15 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        {updates[currentIndex - 1]?.image && (
                                            <CachedImage
                                                src={updates[currentIndex - 1].image}
                                                alt={updates[currentIndex - 1].title}
                                            />
                                        )}
                                        <h2>{updates[currentIndex - 1].title}</h2>
                                        <p className="date">{updates[currentIndex - 1].date}</p>
                                    </motion.div>
                                </div>
                            )}

                            {/* Navigation Arrows */}
                            {updates.length >= 2 && (
                                <>

                                    {currentIndex !== updates.length - 1 && (
                                        <div
                                            className={`arrow left-arrow ${currentIndex >= updates.length - 1 ? "disabled" : ""}`}
                                            onClick={nextUpdate}
                                            aria-label="Next Update"
                                            role="button"
                                            tabIndex={0}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    nextUpdate();
                                                }
                                            }}
                                        >
                                            {"<"}
                                        </div>
                                    )}
                                    {currentIndex !== 0 && (
                                        <div
                                            className="arrow right-arrow"
                                            onClick={prevUpdate}
                                            aria-label="Previous Update"
                                            role="button"
                                            tabIndex={0}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    prevUpdate();
                                                }
                                            }}
                                        >
                                            {">"}
                                        </div>)}
                                </>
                            )}

                            {/* Loading Indicator */}
                            {isFetching && (
                                <div className="loading-indicator">
                                    <Typography variant="body1">{t("news.loading")}</Typography>
                                </div>
                            )}

                        </>
                    )}
                </div>

            </Box>
        </Modal>
    );

};

export default NewUpdatesModal;
